import { useEffect, useMemo, useRef, useState } from "react"
import { MapContainer, Marker, AttributionControl, useMap, useMapEvents } from "react-leaflet"
import L from "leaflet"
import "leaflet/dist/leaflet.css"
import { PageComponent } from "../../components"
import { API_BASE_URL } from '../../config'
import { MapTiles, MAP_MAX_ZOOM } from './components/map-tiles'
import { useUserPoints } from './components/user-points'
import { MapPlace, LatLng, MapSheet, PointsFilter, distanceMeters } from './components/places-sheet'
import './style.css'

const API_URL = `${API_BASE_URL}/get-city-map`

// Центр города (Барнаул)
// const CITY_CENTER: [number, number] = [53.346882, 83.777334]
const PLACE_ZOOM = 15
// С этого зума рядом с точками подписываем названия
const LABEL_ZOOM = 14

// Бэк принимает радиус не больше 50 км
const MAX_RADIUS_METERS = 50000
// Нижнюю часть карты закрывает шторка — выбранное место показываем чуть выше центра
const SHEET_OFFSET_PX = 120

interface ApiStore {
    id: number;
    title: string;
    subtitle: string;
    image?: string;
    location: string;
    distance: number | null;
    raiting: number | null;
    raiting_count: number;
    spending_points?: number;
    obtaining_points?: number;
}

interface CityMapResponse {
    stores: ApiStore[];
    store: ApiStore | null;
}

// Магазин из /map/<id> — его покажем сразу
const getStoreId = (): string | null => {
    const id = window.location.pathname.split('/').filter(Boolean)[1]
    return id && /^\d+$/.test(id) ? id : null
}


// Формат location на бэке — "lat,lon"
const toPlace = (store: ApiStore): MapPlace | null => {
    const [lat, lng] = (store.location ?? '').split(',').map(Number) 
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return {
        storeId: store.id,
        title: store.title,
        subtitle: store.subtitle,
        image: store.image,
        rating: store.raiting ?? null,
        obtainingPoints: store.obtaining_points ?? 0,
        spendingPoints: store.spending_points ?? 0,
        lat,
        lng,
    }
}

// Фильтр шторки → параметр points у /get-city-map
const toPointsParam = ({obtaining, spending}: PointsFilter) =>
    obtaining && spending ? 'both' : obtaining ? 'obtaining' : spending ? 'spending' : null

const fetchCityMap = async (center: LatLng, radius: number, storeId: string | null, filter: PointsFilter, signal: AbortSignal): Promise<CityMapResponse> => {
    const params = new URLSearchParams({location: `${center.lat},${center.lng}`, radius: String(radius)})
    if (storeId) params.set('store_id', storeId)
    const points = toPointsParam(filter)
    if (points) params.set('points', points)
    const res = await fetch(`${API_URL}?${params}`, {signal})
    if (!res.ok) throw new Error('request failed')
    return res.json()
}

const escapeHtml = (text: string) =>
    text.replace(/[&<>"']/g, (ch) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[ch]!)

// Подпись лежит внутри иконки, поэтому клик по ней открывает попап как клик по точке
const createPlaceIcon = (title: string) => L.divIcon({
    className: 'map-place-icon',
    html: `<span class="map-place-dot"></span><span class="map-place-label">${escapeHtml(title)}</span>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -14],
})

// Видимая область карты: запрашиваем точки в круге, описанном вокруг экрана
const getViewport = (map: L.Map) => {
    const center = map.getCenter()
    const corner = map.getBounds().getNorthEast()
    const radius = Math.ceil(distanceMeters(center, {lat: corner.lat, lng: corner.lng}))
    return {center: {lat: center.lat, lng: center.lng}, radius: Math.min(Math.max(radius, 1), MAX_RADIUS_METERS)}
}

// Подписи показываем через класс на контейнере, чтобы не перерисовывать маркеры на каждый зум
const LabelsToggle = () => {
    const map = useMap()
    const update = () => { map.getContainer().classList.toggle('map-show-labels', map.getZoom() >= LABEL_ZOOM) }
    useMapEvents({zoomend: update})
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(update, [map])
    return null
}

// Тап по пустому месту карты закрывает карточку выбранного места
const MapClickHandler = ({onClick}: {onClick: () => void}) => {
    useMapEvents({click: onClick})
    return null
}

const ViewportWatcher = ({onChange}: {onChange: (center: LatLng, radius: number) => void}) => {
    const map = useMap()
    // moveend срабатывает и после перетаскивания, и после зума
    useMapEvents({moveend: () => {
        const {center, radius} = getViewport(map)
        onChange(center, radius)
    }})
    // Первая загрузка — вокруг стартового центра карты
    useEffect(() => {
        const {center, radius} = getViewport(map)
        onChange(center, radius)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map])
    return null
}

export const MapPage = () => {
    const [userLocation, setUserLocation] = useState<LatLng | null>(null)
    const userPoints = useUserPoints()
    const mapRef = useRef<L.Map>(null)
    const markerRefs = useRef<Record<number, L.Marker | null>>({})
    const [CITY_CENTER, SET_SITY_CENTER] = useState<[number, number]>([55.7522, 37.6156]);
    const [places, setPlaces] = useState<MapPlace[]>([])
    const [loading, setLoading] = useState(true)
    const [selectedPlace, setSelectedPlace] = useState<MapPlace | null>(null)
    const [filter, setFilter] = useState<PointsFilter>({obtaining: false, spending: false})
    const filterRef = useRef(filter)
    const viewportRef = useRef<{center: LatLng, radius: number} | null>(null)
    const storeIdRef = useRef(getStoreId())
    const requestRef = useRef<AbortController | null>(null)

    // Центр карты, при котором точка оказывается над шторкой, а не под ней
    const centerAbove = (place: MapPlace, zoom: number) => {
        const map = mapRef.current!
        return map.unproject(map.project([place.lat, place.lng], zoom).add([0, SHEET_OFFSET_PX]), zoom)
    }

    const loadPlaces = (center: LatLng, radius: number) => {
        viewportRef.current = {center, radius}
        requestRef.current?.abort()
        const controller = new AbortController()
        requestRef.current = controller
        setLoading(true)
        // store_id нужен только при первом запросе — дальше выбранное место уже есть
        const storeId = storeIdRef.current
        fetchCityMap(center, radius, storeId, filterRef.current, controller.signal)
            .then(({stores, store}) => {
                setPlaces(stores.map(toPlace).filter((p): p is MapPlace => p !== null))
                setLoading(false)
                if (!storeId) return
                storeIdRef.current = null
                const place = store && toPlace(store)
                if (!place || !mapRef.current) return
                mapRef.current.setView(centerAbove(place, PLACE_ZOOM), PLACE_ZOOM, {animate: false})
                setSelectedPlace(place)
            })
            .catch((error) => {
                if (error.name === 'AbortError') return
                setLoading(false)
                console.warn('[map] не удалось загрузить места:', error)
            })
    }

    useEffect(() => () => requestRef.current?.abort(), [])

    // Сменили фильтр — перезапрашиваем текущую область
    const changeFilter = (value: PointsFilter) => {
        filterRef.current = value
        setFilter(value)
        const viewport = viewportRef.current
        if (viewport) loadPlaces(viewport.center, viewport.radius)
    }

    const flyToUser = (location: LatLng) => {
        mapRef.current?.flyTo([location.lat, location.lng], Math.max(mapRef.current.getZoom(), PLACE_ZOOM - 1), {duration: 0.8})
    }

    const locate = () => {
        if (userLocation) {
            flyToUser(userLocation)
            return
        }
        navigator.geolocation?.getCurrentPosition(
            ({coords}) => {
                const location = {lat: coords.latitude, lng: coords.longitude}
                setUserLocation(location)
                flyToUser(location)
            },
            (error) => console.warn('[map] не удалось получить координаты:', error.message),
            {enableHighAccuracy: true, timeout: 10000},
        )
    }

    // Координаты пользователя: карта перелетает к нему, и moveend дозапрашивает ближайшие места
    useEffect(() => {
        if (!navigator.geolocation) {
            console.warn('[map] геолокация не поддерживается браузером')
            return
        }
        navigator.geolocation.getCurrentPosition(
            ({coords}) => {
                const location = {lat: coords.latitude, lng: coords.longitude}
                console.log('[map] координаты пользователя', {...location, accuracy: coords.accuracy})
                setUserLocation(location)
                SET_SITY_CENTER([location.lat, location.lng])
                const map = mapRef.current
                // Если открыли конкретное место — не уводим карту с него
                if (!map || getStoreId()) return
                map.flyTo([location.lat, location.lng], undefined, {animate: false})
            },
            (error) => console.warn('[map] не удалось получить координаты:', error.message),
            {enableHighAccuracy: true, timeout: 10000},
        )
    }, [])

    // Выбранное место может быть дальше радиуса — показываем его в любом случае
    const visiblePlaces = selectedPlace && !places.some((p) => p.storeId === selectedPlace.storeId)
        ? [selectedPlace, ...places]
        : places

    // Иконки пересоздаём только при смене названий, иначе Leaflet заменял бы DOM маркера на каждый рендер
    const placeIcons = useMemo(
        () => Object.fromEntries(visiblePlaces.map((p) => [p.storeId, createPlaceIcon(p.title)])),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [visiblePlaces.map((p) => `${p.storeId}:${p.title}`).join('|')],
    )

    // Подсветку выбранной точки вешаем классом, чтобы не пересоздавать иконки
    useEffect(() => {
        Object.entries(markerRefs.current).forEach(([id, marker]) => {
            marker?.getElement()?.classList.toggle('is-selected', Number(id) === selectedPlace?.storeId)
        })
    }, [selectedPlace, placeIcons])

    const selectPlace = (place: MapPlace) => {
        setSelectedPlace(place)
        const map = mapRef.current
        if (!map) return
        const zoom = Math.max(map.getZoom(), PLACE_ZOOM)
        map.flyTo(centerAbove(place, zoom), zoom, {duration: 0.8})
    }

    return (
        <PageComponent center={false}>
            <div className="map-page">
                <MapContainer ref={mapRef} className="map-container" center={CITY_CENTER} zoom={12} maxZoom={MAP_MAX_ZOOM} zoomControl={false} attributionControl={false}>
                    <MapTiles />
                    {/* Низ экрана занят шторкой — атрибуцию переносим наверх */}
                    <AttributionControl position="topright" prefix={false} />
                    <ViewportWatcher onChange={loadPlaces} />
                    <MapClickHandler onClick={() => setSelectedPlace(null)} />
                    <LabelsToggle />
                    {visiblePlaces.map((place) => (
                        <Marker
                            key={place.storeId}
                            ref={(marker) => { markerRefs.current[place.storeId] = marker }}
                            position={[place.lat, place.lng]}
                            icon={placeIcons[place.storeId]}
                            eventHandlers={{click: () => selectPlace(place)}}
                        />
                    ))}
                </MapContainer>

                <MapSheet
                    places={visiblePlaces}
                    selected={selectedPlace}
                    userLocation={userLocation}
                    userPoints={userPoints}
                    loading={loading}
                    filter={filter}
                    onFilterChange={changeFilter}
                    onSelect={selectPlace}
                    onDeselect={() => setSelectedPlace(null)}
                    onLocate={locate}
                />
            </div>
        </PageComponent>
    )
}
